import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextField } from "@mui/material";
import { useRef, useEffect } from "react";

import { useTranslation } from "react-i18next";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { useAppSelector } from "@/redux/hooks";

import {
	CreateTaskAndSubtaskComment,
	CreateTaskAndSubtaskCommentSchema,
} from "../schema/tasks-and-subtasks-activity";
import { ActivityTypes } from "../enum/ActivityTypes.enum";
import { TaskInfo, SubTaskInfo } from "../schema/tasks-and-subtasks";
import { useToDoSocket } from "../../ToDo/ToDoList/providers/SocketContext";

interface CreateCommentProps {
	taskData: TaskInfo | SubTaskInfo;
	onClose?: () => void;
}

export default function CreateCommentInput({ onClose, taskData }: CreateCommentProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`to-do.${key}`);

	const inputRef = useRef<HTMLDivElement | null>(null);

	const auth = useAppSelector(authSelector);
	const companyId = auth?.user?.company?.id;

	const defaultValues = {
		taskId: taskData?.isTask ? taskData?.id : null,
		type: ActivityTypes.COMMENT,
		subtaskId: taskData?.isTask ? null : taskData?.id,
		content: "",
		companyId,
	};

	const { control, handleSubmit, reset } = useForm<CreateTaskAndSubtaskComment>({
		defaultValues,
		resolver: zodResolver(CreateTaskAndSubtaskCommentSchema),
	});

	const { createComment } = useToDoSocket();

	const onSubmit = async (values: CreateTaskAndSubtaskComment) => {
		try {
			createComment({
				...values,
				companyId,
				type: ActivityTypes.COMMENT,
				taskId: taskData.isTask ? taskData.id : undefined,
				subtaskId: !taskData.isTask ? taskData.id : undefined,
			});

			reset(defaultValues);
		} catch (err) {
			console.error(err);
		} finally {
			onClose?.();
		}
	};

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
				onClose?.();
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [onClose]);

	return (
		<Controller
			control={control}
			name="content"
			render={({ field }) => (
				<TextField
					{...field}
					fullWidth
					multiline
					inputRef={inputRef}
					label={ts("addComment")}
					maxRows={8}
					minRows={2}
					size="small"
					sx={{
						"& .MuiFilledInput-root": {
							backgroundColor: "white",
							"&:hover": { backgroundColor: "white" },
							"&.Mui-focused": { backgroundColor: "white" },
						},
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter" && !e.shiftKey) {
							e.preventDefault();

							handleSubmit(onSubmit)();
						}
					}}
				/>
			)}
		/>
	);
}
